function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useContext, useMemo } from "react";
import Select from "react-select";
import { CREDITS_DROPDOWN_OPTIONS, formatCreditPrice } from "constants/pricing/credits-config";
import { GenericConfigContext } from "/contexts/generic-config-context";
import { getCurrencyName } from "services/pricing-service";
import { trackEvent } from "services/bigbrain-service";
import { PRICING_PAGE_AI_CREDITS_DROPDOWN_OPENED, PRICING_PAGE_AI_CREDITS_BUCKET_CHANGED } from "constants/bigbrain-event-types";
import { StyledCreditsSelect } from "./credits-select-component.scss";
const formatOptionLabel = ({ label }, { context })=>{
    if (context === "value") {
        return /*#__PURE__*/ _jsx("span", {
            className: "credits-option-label credits-option-value",
            children: label
        });
    }
    return /*#__PURE__*/ _jsx("div", {
        className: "credits-option",
        children: /*#__PURE__*/ _jsx("span", {
            className: "credits-option-label",
            children: label
        })
    });
};
const customStyles = {
    singleValue: (base)=>_object_spread_props(_object_spread({}, base), {
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            maxWidth: "100%",
            fontSize: "14px",
            color: "#676879"
        })
};
const compactStyles = {
    control: (base)=>_object_spread_props(_object_spread({}, base), {
            minHeight: "32px",
            fontSize: "0.75rem",
            padding: 0
        }),
    singleValue: (base)=>_object_spread_props(_object_spread({}, base), {
            fontSize: "0.75rem",
            color: "#676879",
            maxWidth: "calc(100% - 16px)"
        }),
    valueContainer: (base)=>_object_spread_props(_object_spread({}, base), {
            padding: "0 4px"
        }),
    dropdownIndicator: (base)=>_object_spread_props(_object_spread({}, base), {
            padding: "2px",
            svg: {
                width: "16px",
                height: "16px"
            }
        }),
    indicatorSeparator: ()=>({
            display: "none"
        })
};
export const CreditsSelect = ({ tier, onCreditsChange, compact, period: periodProp, selectedValue, placement })=>{
    var _options_;
    const { translate, currency: currencyFromContext, forceCurrency, cloudfrontViewerCountry, localeId } = useContext(GenericConfigContext);
    const currency = currencyFromContext || getCurrencyName({
        forceCurrency,
        countryCode: cloudfrontViewerCountry
    });
    const period = periodProp || "yearly";
    const options = useMemo(()=>{
        const tierOptions = CREDITS_DROPDOWN_OPTIONS[tier];
        if (!tierOptions) return [];
        const labelTemplate = translate("pricingPage.credits.select.label") || "";
        return tierOptions.map(({ value })=>{
            const priceLabel = formatCreditPrice({
                credits: value,
                period,
                currency,
                localeId
            });
            const baseLabel = labelTemplate.replace("{credits}", value.toLocaleString());
            return {
                value,
                label: compact ? baseLabel : `${baseLabel} (${priceLabel})`,
                priceLabel
            };
        });
    }, [
        tier,
        compact,
        currency,
        period,
        localeId,
        translate
    ]);
    const currentValue = selectedValue !== undefined ? selectedValue : (_options_ = options[0]) === null || _options_ === void 0 ? void 0 : _options_.value;
    const onSelectionChange = useCallback(({ value })=>{
        trackEvent(PRICING_PAGE_AI_CREDITS_BUCKET_CHANGED, {
            info1: value,
            info2: currentValue,
            info3: tier,
            placement
        });
        onCreditsChange(value);
    }, [
        onCreditsChange,
        currentValue,
        tier,
        placement
    ]);
    const onMenuOpen = useCallback(()=>{
        trackEvent(PRICING_PAGE_AI_CREDITS_DROPDOWN_OPENED, {
            placement,
            info1: tier
        });
    }, [
        placement,
        tier
    ]);
    if (!options.length) return null;
    return /*#__PURE__*/ _jsx(StyledCreditsSelect, {
        className: "credits-select",
        children: /*#__PURE__*/ _jsx(Select, {
            name: "credits-selection",
            className: "credits-selection",
            classNamePrefix: "credits-selection",
            styles: compact ? compactStyles : customStyles,
            isClearable: false,
            isSearchable: false,
            onChange: onSelectionChange,
            onMenuOpen: onMenuOpen,
            value: options.find((o)=>o.value === selectedValue) || options[0],
            options: options,
            formatOptionLabel: formatOptionLabel
        })
    });
};
